import React from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Stack,
    useTheme,
    CircularProgress,
    IconButton 
} from '@mui/material';
import { Person, AccessTime, CalendarToday, Cancel } from '@mui/icons-material';
import BirdLetter from '../../assets/bird_letter.svg?react';
import EditLocationIcon from '../../assets/ic_baseline-edit-location-alt.svg?react';
import { ObservationListProps } from '../../interfaces/Observation';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useResponsive from '../../hooks/useResponsive';
import BirdIcon from '../../assets/Bird.svg?react';
import BatIcon from '../../assets/Bat.svg?react';
import MammalsIcon from '../../assets/Mammals.svg?react';
import OthersIcon from '../../assets/others.svg?react';
import observationApiService from '../../apiService/ObservationApiService';
import ConfirmationAlert from '../ConfirmationAlert';
import { useAlert } from '../../contexts/AlertContext';
import { timeFormat } from '../../utils/commonMethods';

const ObservationList: React.FC<ObservationListProps> = ({
    observations,
    isLoading = false,
    parentLoading = false,
    height = 'auto',
    editMode = false,
    selectedObservationId = null,
    onObservationSelect
}) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { isMobile } = useResponsive();
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    // Sort observations by latest first
    const sortObservations = (obsArr: any[]) => {
        return [...obsArr].sort((a, b) => {
            const dateA = new Date(`${a.date} ${a.time}`);
            const dateB = new Date(`${b.date} ${b.time}`);
            return dateB.getTime() - dateA.getTime();
        });
    };
    const PAGE_SIZE = 20;
    const [localObservations, setLocalObservations] = React.useState(sortObservations(observations));
    const [visibleCount, setVisibleCount] = React.useState(PAGE_SIZE);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
    const [observationToDelete, setObservationToDelete] = React.useState<any>(null);
    const [isNavigating, setIsNavigating] = React.useState(false);
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);
    const sentinelRef = React.useRef<HTMLDivElement>(null);

    // Handler for navigating to edit observation form
    const handleEditObservation = (observation: any) => {
    setIsNavigating(true);
    navigate(`/observation/${observation.obs_species_id}`);
    };

    // Handler for selecting an observation in edit mode
    const handleSelectObservation = (observationId: number, e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent card click navigation
        if (editMode && onObservationSelect) {
            onObservationSelect(observationId);
        }
    };
    
    // Check if an observation is selected
    const isObservationSelected = (observationId: number) => {
        return editMode && selectedObservationId === observationId;
    };
    
    // Helper function to render species icon based on species_group_id and species_subgroup_id
    const renderSpeciesIcon = (speciesGroupId: number, speciesSubgroupId: number) => {
        let IconComponent;
        if (speciesGroupId === 10) {
            IconComponent = BirdIcon; // Birds
        } else if (speciesGroupId === 11 && speciesSubgroupId === 30) {
            IconComponent = BatIcon; // Bats
        } else if (speciesGroupId === 11) {
            IconComponent = MammalsIcon; // Mammals
        } else {
            IconComponent = OthersIcon; // Others
        }
        
        return (
            <IconComponent
                width={32}
                height={32}
                style={{
                    filter: 'brightness(0) invert(1)',
                    display: 'block'
                }}
            />
        );
    };

    // Format date for display
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('nl-NL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Local state for observations to update after delete; reset pagination on new data
    React.useEffect(() => {
        setLocalObservations(sortObservations(observations));
        setVisibleCount(PAGE_SIZE);
    }, [observations]);

    // Infinite scroll: load next 20 when sentinel enters viewport / scroll container
    React.useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisibleCount(prev => Math.min(prev + PAGE_SIZE, localObservations.length));
                }
            },
            {
                root: isMobile ? null : scrollContainerRef.current,
                threshold: 0.1,
            }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [localObservations.length, isMobile]);

    // Helper to truncate text after N characters (used on mobile to show ellipsis after 4 letters)
    const truncateAfter = (text: string | undefined | null, n = 4) => {
        if (!text) return '';
        return text.length > n ? `${text.slice(0, n)}...` : text;
    };

    const handleDeleteClick = (observation: any, e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent card click navigation
        setObservationToDelete(observation);
        setDeleteConfirmOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!observationToDelete) return;
        try {
            const response = await observationApiService.deleteObservation(observationToDelete.obs_species_id);
            if (response && (response.status_code === 1 || response.status === true)) {
                setLocalObservations(prev => prev.filter(o => o.obs_species_id !== observationToDelete.obs_species_id));
                showAlert(t('observation.deleteSuccess', 'Observation deleted successfully'), 'success');
                // Dispatch a custom event to trigger map reload/clear cache
                window.dispatchEvent(new CustomEvent('reloadMap'));
            } else {
                showAlert(t('observation.deleteFailed', 'Failed to delete observation'), 'error');
            }
        } catch (error) {
            showAlert(t('observation.deleteError', 'Error deleting observation'), 'error');
        }
    };

    const handleCloseConfirmDelete = () => {
        setDeleteConfirmOpen(false);
        setObservationToDelete(null);
    };

    // Show the list loader only when observations are loading, the parent/global loader is not visible,
    if (isLoading && !parentLoading && !isNavigating && localObservations.length === 0) {
        return (
            <Box
                sx={{
                    width: "90%",
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: height !== 'auto' ? height : 'auto',
                    mt: 2
                }}
            >
                <CircularProgress size={40} color="primary" />
            </Box>
        );
    }
    // If no observations, show the empty state
    if (!isLoading && localObservations.length === 0) {
        return (
            <Box
                sx={{
                    width: "90%",
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: height !== 'auto' ? height : 'auto',
                    mt: 2
                }}
            >
                <BirdLetter />
                <Typography
                    sx={{
                        color: 'primary.main',
                        fontFamily: theme.typography.fontFamily,
                        fontSize: 16,
                        mt: 2,
                        textAlign: 'center'
                    }}
                >
                    {t("taskDetails.noObservations") || "No observations available"}
                </Typography>
            </Box>
        );
    }

    // Calculate available height for the list (total height minus title and spacing)
    const titleHeight = isMobile ? 60 : 45; // Reduced height for desktop/tablet due to smaller margins
    const availableHeight = isMobile
        ? 'auto' // No height restriction for mobile - let it scroll naturally
        : height !== 'auto'
            ? `calc(${typeof height === 'string' ? height : `${height}px`} - ${titleHeight}px)`
            : '400px';
    return (
        <Box
            sx={{
                width: { xs: '100%', sm: '90%' },
                px: { xs: 0, sm: 0 },
                height: isMobile ? 'auto' : (height !== 'auto' ? height : 'auto'),
                display: 'flex',
                flexDirection: 'column',
                mt: 2
            }}
        >
            {/* Section Title */}
            <Typography
                variant="h6"
                sx={{
                    fontFamily: "Nunito Sans Regular700",
                    color: theme.palette.primary.main,
                    mb: isMobile ? 2 : 1, // Reduce margin for desktop/tablet
                    fontSize: '16px',
                    flexShrink: 0
                }}
            >
                {"Waarnemingen van Fauna"}
            </Typography>

            {/* Observations List */}
            <Box
                ref={scrollContainerRef}
                sx={{
                    height: availableHeight,
                    overflowY: isMobile ? 'visible' : 'auto', // No scroll restriction on mobile
                    pr: 1,
                    flex: isMobile ? 'none' : 1, // Don't use flex:1 on mobile
                    ...(!isMobile && {
                        '&::-webkit-scrollbar': {
                            width: '6px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: theme.palette.grey[100],
                            borderRadius: '3px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: theme.palette.primary.light,
                            borderRadius: '3px',
                            '&:hover': {
                                background: theme.palette.primary.main,
                            },
                        },
                    })
                }}
            >
                <Stack spacing={2}>
                    {localObservations.slice(0, visibleCount).map((observation) => {
                        const isSelected = isObservationSelected(observation.obs_species_id);
                        return (
                            <Card
                                key={observation.obs_species_id}
                                sx={{
                                    borderRadius: 2,
                                    boxShadow: isSelected ? '0 4px 16px rgba(0, 0, 0, 0.25)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                                    border: isSelected
                                        ? `2px solid ${theme.palette.primary.main}`
                                        : `1px solid ${theme.palette.grey[200]}`,
                                    backgroundColor: 'white',
                                    cursor: 'pointer',
                                    width: { xs: '100%', sm: 'auto' },
                                    boxSizing: 'border-box',
                                    ml: { xs: 0 },
                                    mr: { xs: 0 },
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                        transform: 'translateY(-1px)',
                                    },
                                }}
                                onClick={() => handleEditObservation(observation)}
                            >
                                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        {/* Species Icon */}
                                        <Box
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                backgroundColor: theme.palette.primary.main,
                                                borderRadius: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                            }}
                                        >
                                            {renderSpeciesIcon(observation.species_group_id, observation.species_subgroup_id)}
                                        </Box>

                                        {/* Observation Details */}
                                        <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
                                            {/* Edit Location Button */}
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (onObservationSelect) {
                                                        onObservationSelect(observation.obs_species_id);
                                                    }
                                                    if (isMobile) {
                                                        // Scroll to top to show the map
                                                        setTimeout(() => {
                                                            const mapEl = document.getElementById('map');
                                                            if (mapEl) {
                                                                mapEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                            }
                                                        }, 100);
                                                    }
                                                }}
                                                sx={{
                                                    position: 'absolute',
                                                    top: isMobile ? -8 : -4,
                                                    right: isMobile ? 24 : 28,
                                                    zIndex: 1,
                                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255, 255, 255, 1)',
                                                    },
                                                }}
                                            >
                                                <EditLocationIcon width={20} height={20} />
                                            </IconButton>

                                            {/* Delete Button */}
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={(e) => handleDeleteClick(observation, e)}
                                                sx={{
                                                    position: 'absolute',
                                                    top: isMobile ? -8 : -4,
                                                    right: isMobile ? -8 : -4,
                                                    zIndex: 1,
                                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255, 255, 255, 1)',
                                                    },
                                                }}
                                            >
                                                <Cancel fontSize="small" />
                                            </IconButton>

                                            {isMobile ? (
                                                // Mobile layout: vertical stacking
                                                <>
                                                    {/* Species Name */}
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            fontFamily: "Nunito Sans Regular700",
                                                            fontSize: '13px',
                                                            color: theme.palette.text.primary,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            mb: 0.5,
                                                            pr: 4,
                                                            width: '100%'
                                                        }}
                                                    >
                                                        {observation.species_name}
                                                    </Typography>

                                                    {/* Observer Info */}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, pr: 4 }}>
                                                        <Person sx={{ fontSize: '16px', color: theme.palette.primary.main, flexShrink: 0 }} />
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontSize: '13px',
                                                                fontFamily: "Nunito Sans Regular700",
                                                                color: theme.palette.text.primary,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap'
                                                            }}
                                                        >
                                                            {observation.observer_name}
                                                        </Typography>
                                                    </Box>

                                                    {/* Date and Time */}
                                                    <Stack
                                                        direction="row"
                                                        spacing={2}
                                                        alignItems="center"
                                                        sx={{
                                                            pr: 4,
                                                            width: '100%'
                                                        }}
                                                    >
                                                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flex: '0 1 auto' }}>
                                                            <CalendarToday
                                                                sx={{
                                                                    fontSize: '14px',
                                                                    color: theme.palette.primary.main,
                                                                    flexShrink: 0
                                                                }}
                                                            />
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: theme.palette.text.secondary,
                                                                    fontSize: '13px',
                                                                    fontFamily: "Nunito Sans Regular500",
                                                                    // fontWeight: 400,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            >
                                                                {formatDate(observation.date)}
                                                            </Typography>
                                                        </Stack>

                                                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flex: '0 1 auto' }}>
                                                            <AccessTime
                                                                sx={{
                                                                    fontSize: '14px',
                                                                    color: theme.palette.primary.main,
                                                                    flexShrink: 0
                                                                }}
                                                            />
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: theme.palette.text.secondary,
                                                                    fontSize: '13px',
                                                                    fontFamily: "Nunito Sans Regular500",
                                                                    // fontWeight: 400,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            >
                                                                {timeFormat(observation.time)}
                                                            </Typography>
                                                        </Stack>
                                                    </Stack>
                                                </>
                                            ) : (
                                                // Desktop layout: original horizontal layout
                                                <>
                                                    {/* Species Name and Observer */}
                                                    <Stack
                                                        direction="row"
                                                        spacing={1}
                                                        alignItems="center"
                                                        sx={{
                                                            mb: 1,
                                                            pr: 2,
                                                            width: '100%',
                                                            minWidth: 0,
                                                            flexWrap: 'wrap'
                                                        }}
                                                    >
                                                        {/* Species Name */}
                                                        <Typography
                                                            variant="h6"
                                                            noWrap
                                                            sx={{
                                                                fontFamily: "Nunito Sans Regular700",
                                                                fontSize: '12px',
                                                                color: theme.palette.text.primary,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                                flexShrink: 1,
                                                                maxWidth: '60%'
                                                            }}
                                                        >
                                                            {observation.species_name}
                                                        </Typography>

                                                        {/* Observer Info */}
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                                                            <Person sx={{ fontSize: '16px', color: theme.palette.primary.main }} />
                                                            <Typography
                                                                variant="body2"
                                                                noWrap
                                                                sx={{
                                                                    fontSize: '12px',
                                                                    fontFamily: "Nunito Sans Regular700",
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                    flexShrink: 1,
                                                                    maxWidth: '100%'
                                                                }}
                                                            >
                                                                {observation.observer_name}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>

                                                    {/* Date and Time */}
                                                    <Stack
                                                        direction="row"
                                                        spacing={2}
                                                        alignItems="center"
                                                        sx={{
                                                            pr: 2,
                                                            width: '100%',
                                                            minWidth: 0
                                                        }}
                                                    >
                                                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                                                            <CalendarToday
                                                                sx={{
                                                                    fontSize: '14px',
                                                                    color: theme.palette.primary.main
                                                                }}
                                                            />
                                                            <Typography
                                                                variant="body2"
                                                                noWrap
                                                                sx={{
                                                                    color: theme.palette.text.secondary,
                                                                    fontSize: '12px',
                                                                    fontFamily: theme.typography.fontFamily,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                }}
                                                            >
                                                                {formatDate(observation.date)}
                                                            </Typography>
                                                        </Stack>

                                                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                                                            <AccessTime
                                                                sx={{
                                                                    fontSize: '14px',
                                                                    color: theme.palette.primary.main
                                                                }}
                                                            />
                                                            <Typography
                                                                variant="body2"
                                                                noWrap
                                                                sx={{
                                                                    color: theme.palette.text.secondary,
                                                                    fontSize: '12px',
                                                                    fontFamily: theme.typography.fontFamily,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                }}
                                                            >
                                                                {timeFormat(observation.time)}
                                                            </Typography>
                                                        </Stack>
                                                    </Stack>
                                                </>
                                            )}
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        );
                    })}
                </Stack>
                <div ref={sentinelRef} style={{ height: 1 }} />
                    </Box>

                    {/* Confirmation Dialog */}
                    <ConfirmationAlert
                        open={deleteConfirmOpen}
                        onDelete={handleConfirmDelete}
                        message={
                            observationToDelete
                                ? `Weet u zeker dat u deze observatie ${observationToDelete.species_name} wilt verwijderen?`
                                : "Weet u zeker dat u deze observatie wilt verwijderen?"
                        }
                        handleClose={handleCloseConfirmDelete}
                        yesLabel={"OK"}
                        noLabel={"Annuleren"}
                    />
                </Box>
            );
        };

export default ObservationList;
